import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DollarSign, Download, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";

interface FinancialSummary {
  totalRevenue: number;
  outstanding: number;
  expenses: number;
  profitMargin: number;
  paidInvoices: number;
  unpaidInvoices: number;
}

export function FinancialTab() {
  const { orgId } = useAuth();
  const [summary, setSummary] = useState<FinancialSummary>({
    totalRevenue: 0,
    outstanding: 0,
    expenses: 0,
    profitMargin: 0,
    paidInvoices: 0,
    unpaidInvoices: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId) fetchFinancials();
  }, [orgId]);

  const fetchFinancials = async () => {
    setLoading(true);
    try {
      const monthStart = startOfMonth(new Date()).toISOString();
      const monthEnd = endOfMonth(new Date()).toISOString();

      // Fetch all invoices for this org in the current month
      const { data: invoices, error } = await supabase
        .from("invoices")
        .select("id, total_amount, status, materials_cost, labor_cost")
        .eq("org_id", orgId!)
        .gte("created_at", monthStart)
        .lte("created_at", monthEnd);

      if (error) throw error;

      const paid = (invoices || []).filter((i) => i.status === "paid");
      const unpaid = (invoices || []).filter((i) => i.status !== "paid" && i.status !== "void");

      const totalRevenue = paid.reduce((sum, i) => sum + (i.total_amount || 0), 0);
      const outstanding = unpaid.reduce((sum, i) => sum + (i.total_amount || 0), 0);
      const expenses = (invoices || []).reduce(
        (sum, i) => sum + (i.materials_cost || 0) + (i.labor_cost || 0),
        0
      );
      const profitMargin =
        totalRevenue > 0 ? ((totalRevenue - expenses) / totalRevenue) * 100 : 0;

      setSummary({
        totalRevenue,
        outstanding,
        expenses,
        profitMargin,
        paidInvoices: paid.length,
        unpaidInvoices: unpaid.length,
      });
    } catch (err: any) {
      console.error("Error fetching financials:", err);
      toast.error("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Financial Reports</h2>
          <p className="text-muted-foreground">
            {format(new Date(), "MMMM yyyy")} — Track revenue, expenses, and profitability
          </p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => toast.info("Export coming soon")}>
          <Download className="w-4 h-4" />
          Export Reports
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <TrendingUp className="w-4 h-4 text-green-500" />
          </div>
          <p className="text-3xl font-bold mb-1">{formatCurrency(summary.totalRevenue)}</p>
          <p className="text-xs text-muted-foreground">
            {summary.paidInvoices} paid invoice{summary.paidInvoices !== 1 ? "s" : ""} this month
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-muted-foreground">Outstanding</p>
            <DollarSign className="w-4 h-4 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold mb-1">{formatCurrency(summary.outstanding)}</p>
          <p className="text-xs text-muted-foreground">
            {summary.unpaidInvoices} pending payment{summary.unpaidInvoices !== 1 ? "s" : ""}
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-muted-foreground">Expenses</p>
            <TrendingDown className="w-4 h-4 text-red-500" />
          </div>
          <p className="text-3xl font-bold mb-1">{formatCurrency(summary.expenses)}</p>
          <p className="text-xs text-muted-foreground">Materials + labor this month</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-muted-foreground">Profit Margin</p>
            <TrendingUp className="w-4 h-4 text-primary" />
          </div>
          <p className="text-3xl font-bold mb-1">{summary.profitMargin.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground">Net margin</p>
        </Card>
      </div>

      {/* Revenue breakdown */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Financial Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Revenue Collected</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalRevenue)}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Total Costs</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.expenses)}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Net Profit</p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(summary.totalRevenue - summary.expenses)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
